import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { AlertTriangle, CheckCircle2, Download, ExternalLink } from 'lucide-react'

import { useTenantBranding } from '../../../contexts/TenantContext'
import useSignatureCanvas, { SIGNATURE_CANVAS_STYLE } from '../../../shared/hooks/useSignatureCanvas'

const API_BASE = import.meta.env.VITE_BID_WORKUP_API_URL || '/api/bidworkup'
const GONE = 'This link is no longer valid. Please ask for a new one.'

/**
 * Where a client reads a proposal and signs it. Public: the token in the url is the credential.
 *
 * A SEPARATE PAGE FROM `ClientProposal`, and the two are not variants of each other. That one is a
 * copy link — view and download, forever, and it can never sign anything. This one carries a
 * single-use token that binds the client to a price. Building them as one page with a `canSign`
 * prop would put both capabilities behind one component, one prop away from a copy link that signs.
 *
 * THE DOCUMENT IS ON THE PAGE, ABOVE THE FORM. It is not a download the client is trusted to have
 * opened. Asking somebody to sign something they were merely offered a link to is the shape of the
 * problem, not a shortcut around it — and the bytes framed here are the same bytes the server
 * verifies against `pdf_sha256` before it accepts the signature.
 *
 * THE CONSENT SENTENCE COMES FROM THE SERVER and only its key/version/locale go back. A
 * client-supplied consent string is worthless as evidence, because the party who benefits from the
 * wording would be the party supplying it.
 *
 * DECLINING IS A FIRST-CLASS ACTION, with a required reason, at the owner's instruction. A proposal
 * sitting at "sent" tells a salesperson nothing about whether the client said no or simply has not
 * opened it, and those two call for opposite responses.
 */
export default function ClientProposalSign() {
  const { slug, code } = useParams()
  const brand = useTenantBranding()
  const base = `${API_BASE}/client/proposal/${encodeURIComponent(slug)}/${encodeURIComponent(code)}`

  const [doc, setDoc] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(null)          // 'signed' | 'declined'
  const [copyReady, setCopyReady] = useState(false)

  const [name, setName] = useState('')
  const [title, setTitle] = useState('')
  const [email, setEmail] = useState('')
  const [agreed, setAgreed] = useState(false)
  const [signature, setSignature] = useState('')
  const [declining, setDeclining] = useState(false)
  const [reason, setReason] = useState('')

  const pad = useSignatureCanvas({ value: signature, onChange: setSignature })

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const res = await fetch(`${base}/sign`)
        if (!res.ok) {
          const data = await res.json().catch(() => ({}))
          // The server's words only when they were written for a person. A service-wide 404 handler
          // can flatten a route's message into "The endpoint /api/v1/... was not found", which in
          // front of a customer leaks an internal path and explains nothing.
          const detail = typeof data.detail === 'string' ? data.detail : ''
          const internal = detail.includes('/api/') || detail.toLowerCase().includes('endpoint')
          throw new Error(!detail || internal ? GONE : detail)
        }
        const data = await res.json()
        if (!cancelled) {
          setDoc(data)
          // WHAT WE ALREADY KNOW. The email is the address we posted this link to; the name and
          // title are what this person typed the last time they signed anything for this company.
          // A repeat customer signing their fifth document should not have to introduce themselves
          // a fifth time.
          //
          // SET ONLY IF THE BOX IS STILL EMPTY, so a suggestion can never overwrite something the
          // signer has already typed — the load resolving after they start typing is exactly the
          // race that would otherwise silently replace their own name with an old one.
          const pre = data.prefill || {}
          if (pre.email) setEmail((v) => v || pre.email)
          if (pre.name) setName((v) => v || pre.name)
          if (pre.title) setTitle((v) => v || pre.title)
        }
      } catch (e) {
        if (!cancelled) setError(e.message || GONE)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [base])

  const post = async (path, body) => {
    const res = await fetch(`${base}/${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : GONE)
    return data
  }

  const sign = async () => {
    // Checked here so the client is told which box before a round trip, not because the server
    // trusts it — every one of these is enforced again in `sign_client`.
    if (!name.trim()) return setError('Please type your name.')
    if (!agreed) return setError('Please tick the box to confirm you agree.')
    if (!signature) return setError('Please sign in the box below.')
    setError(null)
    setSubmitting(true)
    try {
      const data = await post('sign', {
        signer_name: name.trim(),
        signer_title: title.trim() || null,
        signer_email: email.trim() || null,
        signature,
        // The server's own field names, echoed back verbatim. `offer()` returns consent_key /
        // consent_version / consent_locale alongside the text, and renaming them on the way through
        // would be one more place for the wording and its identifier to drift apart.
        consent_key: doc.consent.consent_key,
        consent_version: doc.consent.consent_version,
        consent_locale: doc.consent.consent_locale,
      })
      setCopyReady(!!data.signed_copy_ready)
      setDone('signed')
    } catch (e) {
      setError(e.message)
    } finally {
      setSubmitting(false)
    }
  }

  const decline = async () => {
    if (!reason.trim()) return setError('Please say why, so we can come back to you.')
    setError(null)
    setSubmitting(true)
    try {
      await post('decline', { reason: reason.trim(), declined_by: name.trim() || null })
      setDone('declined')
    } catch (e) {
      setError(e.message)
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <Shell brand={brand}>
        <div className="flex h-64 items-center justify-center text-sm text-gray-500">Loading…</div>
      </Shell>
    )
  }

  if (error && !doc) {
    return (
      <Shell brand={brand}>
        <div className="mx-auto max-w-md px-4 py-16 text-center">
          <AlertTriangle className="mx-auto h-8 w-8 text-amber-500" aria-hidden="true" />
          <p className="mt-3 text-sm text-gray-700">{error}</p>
        </div>
      </Shell>
    )
  }

  if (done) {
    return (
      <Shell brand={brand}>
        <Outcome
          kind={done}
          jobName={doc?.job_name}
          copyUrl={done === 'signed' && copyReady ? `${base}/signed-copy` : null}
        />
      </Shell>
    )
  }

  const pdfUrl = `${base}/sign/pdf`

  return (
    <Shell brand={brand}>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 bg-white px-4 py-3">
        <div className="min-w-0">
          <h1 className="truncate text-base font-semibold text-gray-900">Bid Proposal</h1>
          <div className="mt-0.5 text-sm text-gray-600">
            {doc?.job_name}
            {doc?.company_signed_by_name && (
              // NAMED, because it is the most reassuring fact available to somebody being asked to
              // commit to a price: the contractor has already committed to it, by name.
              <span className="ml-2 text-gray-500">
                · already signed by {doc.company_signed_by_name}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Plain anchors, not scripted downloads: they work when the framed viewer does not, and
              on a phone they hand the file to whatever the client normally reads PDFs with. */}
          <a
            href={pdfUrl}
            download={doc?.download_name || 'proposal.pdf'}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <Download className="h-4 w-4" aria-hidden="true" />
            Download
          </a>
          <a
            href={pdfUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <ExternalLink className="h-4 w-4" aria-hidden="true" />
            Open
          </a>
        </div>
      </div>

      {/* THE DOCUMENT, THEN THE FORM, in that order and on the same page. `object` rather than
          `iframe` because it degrades honestly: a browser with no PDF viewer renders the children
          instead of the blank grey rectangle an iframe leaves behind. */}
      <object data={pdfUrl} type="application/pdf" className="h-[60vh] w-full bg-gray-100">
        <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
          <p className="text-sm text-gray-700">Your browser can’t display this document here.</p>
          <a
            href={pdfUrl}
            className="inline-flex items-center gap-1.5 rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800"
          >
            <Download className="h-4 w-4" aria-hidden="true" />
            Open the PDF
          </a>
        </div>
      </object>

      <div className="mx-auto max-w-2xl px-4 py-6">
        <h2 className="text-base font-semibold text-gray-900">Sign this proposal</h2>

        {error && (
          <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <Field label="Your name" required value={name} onChange={setName}
                 autoComplete="name" />
          <Field label="Your title" value={title} onChange={setTitle}
                 autoComplete="organization-title" />
          {/* EDITABLE, always. A pre-filled box is a convenience; a signature under a name the
              signer did not type is us signing for them. Whatever they submit is what the signature
              record stores — the suggestion is never written to a document on its own. */}
          <Field label="Your email" value={email} onChange={setEmail} type="email"
                 autoComplete="email"
                 hint="So we can send you the signed copy." />
        </div>

        {doc?.consent?.text && (
          <label className="mt-5 flex cursor-pointer items-start gap-2.5 rounded-md border border-gray-200 bg-gray-50 px-3 py-3">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300"
            />
            {/* The server's own sentence, rendered as text. Only its key and version travel back. */}
            <span className="text-sm text-gray-800">{doc.consent.text}</span>
          </label>
        )}

        <div className="mt-5">
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm font-medium text-gray-800">
              Sign here <span className="text-red-600">*</span>
            </span>
            <button
              type="button"
              onClick={pad.clear}
              className="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
            >
              {/* Above the pad, not under it: under the pad is where a thumb rests while the other
                  hand steadies the phone, and a mis-tap there costs the signature. */}
              Clear
            </button>
          </div>
          <div className="relative mt-1.5 rounded-md border border-gray-300 bg-white">
            {!pad.signed && !pad.touched && (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-base text-gray-400">
                Sign here
              </div>
            )}
            <canvas
              ref={pad.canvasRef}
              {...pad.handlers}
              aria-label="Signature"
              style={{ ...SIGNATURE_CANVAS_STYLE, height: 170, borderRadius: 6 }}
            />
          </div>
          <p className={`mt-1.5 text-xs ${pad.signed ? 'text-green-600' : 'text-gray-500'}`}>
            {pad.signed ? 'Signed' : 'Not signed yet'}
          </p>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={sign}
            disabled={submitting}
            className="inline-flex items-center gap-2 rounded-md bg-gray-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-gray-800 disabled:opacity-50"
          >
            {submitting ? 'Signing…' : 'Sign and accept'}
          </button>
          {!declining && (
            <button
              type="button"
              onClick={() => { setDeclining(true); setError(null) }}
              className="text-sm font-medium text-gray-600 underline underline-offset-2 hover:text-gray-900"
            >
              I’d rather not accept this
            </button>
          )}
        </div>

        {declining && (
          <div className="mt-5 rounded-md border border-gray-200 bg-gray-50 px-3 py-3">
            <label className="block text-sm font-medium text-gray-800" htmlFor="decline-reason">
              Why are you declining? <span className="text-red-600">*</span>
            </label>
            <p className="mt-0.5 text-xs text-gray-500">
              This goes straight to the person who sent it, so they know whether to come back to you.
            </p>
            <textarea
              id="decline-reason"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
            <div className="mt-3 flex items-center gap-3">
              <button
                type="button"
                onClick={decline}
                disabled={submitting}
                className="rounded-md border border-red-300 bg-white px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
              >
                {submitting ? 'Sending…' : 'Decline this proposal'}
              </button>
              <button
                type="button"
                onClick={() => { setDeclining(false); setReason(''); setError(null) }}
                className="text-sm text-gray-600 underline underline-offset-2"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        <p className="mt-6 text-xs text-gray-500">
          This link works once. Nothing is recorded until you press a button above.
        </p>
      </div>
    </Shell>
  )
}

function Field({ label, value, onChange, required, hint, type = 'text', autoComplete }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-gray-800">
        {label}{required && <span className="text-red-600"> *</span>}
      </span>
      <input
        type={type}
        value={value}
        autoComplete={autoComplete}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
      />
      {hint && <span className="mt-1 block text-xs text-gray-500">{hint}</span>}
    </label>
  )
}

/**
 * What the client sees once they have acted.
 *
 * THE PAGE DOES NOT RELOAD ITSELF. Their token is spent the instant they sign, so re-fetching would
 * answer "this link is no longer valid" one second after they used it correctly — the single most
 * alarming thing you can show somebody who has just signed a contract.
 */
function Outcome({ kind, jobName, copyUrl }) {
  const signed = kind === 'signed'
  return (
    <div className="mx-auto max-w-md px-4 py-16 text-center">
      {signed
        ? <CheckCircle2 className="mx-auto h-10 w-10 text-green-600" aria-hidden="true" />
        : <AlertTriangle className="mx-auto h-10 w-10 text-gray-400" aria-hidden="true" />}
      <h1 className="mt-4 text-lg font-semibold text-gray-900">
        {signed ? 'Signed — thank you' : 'Thanks for letting us know'}
      </h1>
      <p className="mt-2 text-sm text-gray-600">
        {signed
          ? `Your signature on ${jobName || 'this proposal'} has been recorded, and everyone involved has been told.`
          : `We have passed your reason on to the person who sent ${jobName || 'this proposal'}. They will be in touch.`}
      </p>
      {signed && copyUrl && (
        <a
          href={copyUrl}
          className="mt-5 inline-flex items-center gap-2 rounded-md bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800"
        >
          <Download className="h-4 w-4" aria-hidden="true" />
          Download your signed copy
        </a>
      )}
      {signed && !copyUrl && (
        // Honest rather than reassuring. The signed copy is produced after the signature commits and
        // is allowed to fail without failing the signature, so it is genuinely sometimes not ready.
        <p className="mt-5 text-xs text-gray-500">
          Your signed copy is being prepared and will be emailed to you.
        </p>
      )}
    </div>
  )
}

function Shell({ brand, children }) {
  return (
    <div className="min-h-screen bg-gray-50">
      {brand?.company_name && (
        <div className="border-b border-gray-200 bg-white px-4 py-2">
          <p className="text-sm font-medium text-gray-600">{brand.company_name}</p>
        </div>
      )}
      {children}
    </div>
  )
}
